import { LockOutlined, MessageOutlined } from '@ant-design/icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useForm } from 'react-hook-form'

import AtomSwitch, {
  type AtomSwitchOption,
} from '@/components/Atoms/AtomSwitch/AtomSwitch'
import AuthLayout from '@/components/AuthLayout/AuthLayout'
import ByPasswordForm from '@/components/Login/ByPasswordForm/ByPasswordForm'
import ByPhoneForm from '@/components/Login/ByPhoneForm/ByPhoneForm'
import ResetPasswordForm from '@/components/Login/ResetPasswordForm/ResetPasswordForm'
import { ByPasswordFormSchema, ByPhoneFormSchema } from '@/schemas'

import cn from './Login.module.scss'

export type LoginFormValues = {
  phone: string
  password: string
}

type LoginMethod = 'ByPassword' | 'ByPhone'
type LoginForm = LoginMethod | 'ResetPassword'

const LOGIN_METHOD_OPTIONS: AtomSwitchOption<LoginMethod>[] = [
  {
    value: 'ByPassword',
    label: 'По паролю',
    icon: <LockOutlined aria-hidden />,
  },
  { value: 'ByPhone', label: 'По СМС', icon: <MessageOutlined aria-hidden /> },
]

function Login() {
  const [currentForm, setCurrentForm] = useState<LoginForm>('ByPassword')
  const [showVersion, setShowVersion] = useState(false)
  const isResetPassword = currentForm === 'ResetPassword'

  const formMethods = useForm<LoginFormValues>({
    resolver: zodResolver(
      currentForm === 'ByPassword' ? ByPasswordFormSchema : ByPhoneFormSchema
    ),
    defaultValues: {
      phone: '',
      password: '',
    },
  })

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
    getValues,
  } = formMethods

  const handleLogoDoubleClick = () => {
    setShowVersion(true)
    setTimeout(() => setShowVersion(false), 5000)
  }

  return (
    <AuthLayout
      title={isResetPassword ? 'Восстановление пароля' : 'Вход'}
      back={
        isResetPassword
          ? {
              label: 'Вернуться ко входу',
              onClick: () => setCurrentForm('ByPassword'),
            }
          : undefined
      }
      onLogoDoubleClick={handleLogoDoubleClick}
    >
      {showVersion &&
        createPortal(
          <div className={cn.versionInfo}>
            <p>🎉 Версия: {import.meta.env.VITE_APP_VERSION || 'dev'}</p>
            <p>
              📅 Билд:{' '}
              {import.meta.env.VITE_BUILD_DATE ||
                new Date().toLocaleString('ru-RU')}
            </p>
            <p>
              🔖 Коммит: {import.meta.env.VITE_GIT_COMMIT_HASH || 'unknown'}
            </p>
          </div>,
          document.body
        )}
      {currentForm !== 'ResetPassword' && (
        <AtomSwitch
          options={LOGIN_METHOD_OPTIONS}
          value={currentForm}
          onChange={setCurrentForm}
          ariaLabel="Способ входа"
        />
      )}
      {currentForm === 'ByPassword' && (
        <ByPasswordForm
          onForgotPassword={() => setCurrentForm('ResetPassword')}
          control={control}
          errors={errors}
          handleSubmit={handleSubmit}
          setError={setError}
          getValues={getValues}
        />
      )}
      {currentForm === 'ByPhone' && (
        <ByPhoneForm
          control={control}
          errors={errors}
          handleSubmit={handleSubmit}
          setError={setError}
        />
      )}
      {currentForm === 'ResetPassword' && (
        <ResetPasswordForm initialPhone={getValues('phone')} />
      )}
    </AuthLayout>
  )
}

export default Login
